import _ from 'lodash';
import log from '$$log';
import pricingHelper from '$$helpers/pricingHelper';
import { getNormalizedColumnName, verifyColumnsOrder, getCorrectGridWidthAfterResize, hasCostsColRights } from '../helpers';
import ColumnsBar from '../../../../columnsConfigBar/columnsBar';

const constants = {
  buttons: {
    width: 50,
    min_width: 50,
    max_width: 50,
  },
  wbs: {
    width: 45,
    min_width: 45,
    max_width: 45,
  },
  overdue: {
    width: 24,
    min_width: 24,
    max_width: 24,
  },
  overload: {
    width: 24,
    min_width: 24,
    max_width: 24,
  },
  comments: {
    width: 24,
    min_width: 24,
    max_width: 24,
  },
  attachments: {
    width: 24,
    min_width: 24,
    max_width: 24,
  },
};

class ColumnsConfig {
  #instance = null;

  #projectConfig = null;

  #columnsConfig = null;

  init(instance, projectConfig) {
    this.#instance = instance;
    this.#projectConfig = projectConfig;

    this.#prepareConfig(); // Parse initial config from projectConfig.initGridColumns and patch it according to mode
    this.#updateProjectColumnsConfig();
  }

  get config() {
    return this.#columnsConfig;
  }

  setColumnNewWidth(column, newWidth) {
    if (!this.#columnsConfig.sizes[column.name]) {
      this.#columnsConfig.sizes[column.name] = {
        min_width: this.#instance.config.min_grid_column_width,
        width: 0,
      };
    }

    this.#columnsConfig.sizes[column.name].width = newWidth;

    setTimeout(() => {
      const newColumnsWidth = this.getTotalColumnsWidth();
      const windowParams = this.#instance.ganttDataApi.getWindowParams();
      const correctGrid = getCorrectGridWidthAfterResize(newColumnsWidth, windowParams);

      this.#instance.config.grid_width = correctGrid.width;
      this.#columnsConfig.grid_width = this.#instance.config.grid_width;
      this.#updateProjectColumnsConfig();

      if (correctGrid.needRender) {
        const t = log.time('ganttRender.instance');
        this.#instance.render();
        t.end();
      }
    });
  }

  setGridNewWidth(newWidth) {
    const columns = this.#instance.config.columns;
    const windowParams = this.#instance.ganttDataApi.getWindowParams();
    const correctGrid = getCorrectGridWidthAfterResize(newWidth, windowParams);

    this.#instance.config.grid_width = correctGrid.width;

    _.each(columns, (gridColumn, index) => {
      const width = gridColumn.width;

      if (!this.#columnsConfig.sizes[gridColumn.name]) {
        this.#columnsConfig.sizes[gridColumn.name] = {
          min_width: this.#instance.config.min_grid_column_width,
          width: 0,
        };
      }

      this.#columnsConfig.sizes[gridColumn.name].width = width;
    });

    setTimeout(() => {
      this.#columnsConfig.grid_width = this.#instance.config.grid_width;
      this.#updateProjectColumnsConfig();

      if (correctGrid.needRender) {
        const t = log.time('ganttRender.instance');
        this.#instance.render();
        t.end();
      }
    });
  }

  setColumnsOrder(columnsOrder, needUpdateProject = false) {
    this.#columnsConfig.columnsOrder = columnsOrder;
    ColumnsBar.reorderCustomColumns(this.#columnsConfig);
    if (needUpdateProject) {
      this.#updateProjectColumnsConfig();
    }
  }

  hideColumn(columnId) {
    const gridColumn = this.#instance.getGridColumn(columnId);

    gridColumn.hide = true;
    this.#columnsConfig[columnId] = false;

    if (columnId === 'start_date' || columnId === 'end_date') {
      this.#columnsConfig.advancedOptions[columnId].check = false;
    }

    this.#instance.config.grid_width -= gridColumn.width;
    this.#columnsConfig.grid_width = this.#instance.config.grid_width;
    this.#updateProjectColumnsConfig();
  }

  showColumn(columnId) {
    const gridColumn = this.#instance.getGridColumn(columnId);
    const defaultSizes = this.#columnsConfig.sizes.default;

    gridColumn.hide = false;
    this.#columnsConfig[columnId] = true;

    if (columnId === 'start_date' || columnId === 'end_date') {
      this.#columnsConfig.advancedOptions[columnId].check = false;
    }

    this.#adjustColumnsSizesBeforeColumnShow({
      ...gridColumn,
      width: gridColumn.width || defaultSizes.width,
      min_width: gridColumn.min_width || defaultSizes.min_width,
    });
    this.#updateProjectColumnsConfig();
  }

  changeAdvancedOption(columnId, value) {
    this.#columnsConfig.advancedOptions[columnId].check = !!value;
    this.#updateProjectColumnsConfig();
    const t = log.time('ganttRender.instance');
    this.#instance.render();
    t.end();
  }

  setColumnsWidth() {
    this.#instance.config.columns.forEach(column => {
      const sizeObject = this.#columnsConfig.sizes[column.name] || this.#columnsConfig.sizes.default;

      column.width = sizeObject.width;
      column.min_width = sizeObject.min_width;

      if (sizeObject.max_width) {
        column.max_width = sizeObject.max_width;
      }
    });
  }

  checkColumnsVisibilityAndOrder(needUpdateProject = false) {
    const columnsOrder = this.#columnsConfig.columnsOrder ? [...this.#columnsConfig.columnsOrder] : [];

    this.#instance.config.columns.forEach(column => {
      if (this.#columnsConfig[column.name] !== undefined && column.name !== 'masschange') {
        column.hide = !this.#columnsConfig[column.name];

        if (!pricingHelper.checkPricingAccess(getNormalizedColumnName(column))) {
          column.hide = true;
        }
      }
      if (this.#columnsConfig.columnsOrder?.length && !this.#columnsConfig.columnsOrder.includes(column.name)) {
        columnsOrder.push(column.name);
      }
    });

    if (columnsOrder.length) {
      this.setColumnsOrder(verifyColumnsOrder(columnsOrder), needUpdateProject);
      this.#instance.config.columns.sort((a, b) => this.#columnsConfig.columnsOrder.indexOf(a.name) - this.#columnsConfig.columnsOrder.indexOf(b.name));
    }
  }

  #adjustColumnsSizesBeforeColumnShow(column) {
    this.#instance.config.grid_width += column.width;

    if (this.#instance.$grid.clientWidth < this.#instance.config.grid_width) {
      let diff = column.width - (this.#instance.$task.clientWidth - 5);

      if (diff > 0) {
        while (diff > 0) {
          const diffRatio = diff / this.#instance.config.grid_width;

          _.each(this.#columnsConfig.sizes, (value, key) => {
            if (this.#columnsConfig[key] !== false && this.#columnsConfig.sizes[key].width > 50) {
              let widthRatio = parseInt((this.#columnsConfig.sizes[key].width * diffRatio), 10) || 1;

              if (widthRatio > (this.#columnsConfig.sizes[key].width - 50)) {
                widthRatio = this.#columnsConfig.sizes[key].width - 50;
              }

              if (widthRatio > diff) {
                widthRatio = diff;
              }

              this.#columnsConfig.sizes[key].width -= widthRatio;
              diff -= widthRatio;
            }
          });
        }

        this.setColumnsWidth();
        this.#validateColumnsSizes();
        this.#instance.config.grid_width = this.getTotalColumnsWidth();
        this.#columnsConfig.grid_width = this.#instance.config.grid_width;
      }
    }
  }

  getTotalColumnsWidth() {
    return this.#instance.config.columns.reduce((acc, column) => (!column.hide ? (acc + column.width) : acc), 0);
  }

  #updateProjectColumnsConfig() {
    if (this.#instance.ganttDataApi.isBase()) {
      this.#instance.callEvent('checkParamsToUpdateAndUpdate', [{
        key: 'initGridColumns',
        value: this.#columnsConfig,
      }]);
    }
  }

  #prepareConfig() {
    this.#columnsConfig = _.assignIn({
      overdue: true,
      overload: false,
      wbs: true,
      comments: false,
      attachments: false,
      start_date: false,
      end_date: false,
      create_date: false,
      creator: false,
      estimation: false,
      total_price: false,
      actual_cost: false,
      resource_id: true,
      owner_id: false,
      progress: false,
      duration: false,
      time_tracking: false,
      priority: false,
      status: true,
      predecessors: false,
      total_float: false,
      free_float: false,
      jira_key: !!this.#projectConfig.isJira,
    }, this.#projectConfig.initGridColumns || {});

    if (this.#projectConfig.isJira) {
      this.#columnsConfig.creator = false;
    }

    if (this.#instance.ganttDataApi.isExport()) {
      this.#columnsConfig.comments = false;
      this.#columnsConfig.attachments = false;
      this.#columnsConfig.buttons = false;
    }

    if (this.#instance.ganttDataApi.isLink()) {
      this.#columnsConfig.buttons = false;
      this.#columnsConfig.total_float = false;
      this.#columnsConfig.free_float = false;
    }

    if (this.#instance.ganttDataApi.isBase()) {
      this.#columnsConfig.buttons = true;
    }

    if (!hasCostsColRights(this.#instance)) {
      this.#columnsConfig.total_price = false;
      this.#columnsConfig.actual_cost = false;
    }

    this.#setAdvancedOptions();
    this.#setSizes();
    this.#adjustColumnSizes();
  }

  #adjustColumnSizes() {
    const constantSizeColumns = ['wbs', 'comments', 'attachments', 'overload', 'buttons', 'overdue'];
    const columnSizes = this.#columnsConfig.sizes;

    constantSizeColumns.forEach(column => {
      if (!columnSizes[column]) return;

      if (
        +columnSizes[column].width !== constants[column].width
        || +columnSizes[column].min_width !== constants[column].min_width
      ) {
        columnSizes[column] = constants[column];
      }
    });
  }

  #validateColumnsSizes() {
    _.each(this.#columnsConfig.sizes, (size, key) => {
      let newWidth = null;

      if (size.min_width > 0 && size.width < size.min_width) {
        newWidth = size.min_width;
      }

      if (size.max_width > 0 && size.width > size.max_width) {
        newWidth = size.max_width;
      }

      if (newWidth) {
        size.width = newWidth;

        const ganttColumn = _.find(this.#instance.config.columns, { name: key });

        if (ganttColumn) {
          ganttColumn.width = size.width;
        }
      }
    });
  }

  #setAdvancedOptions() {
    this.#columnsConfig.advancedOptions = _.merge({
      start_date: {
        check: false,
      },
      end_date: {
        check: false,
      },
      create_date: {
        check: false,
      },
    }, this.#columnsConfig.advancedOptions || {});
  }

  #setSizes() {
    this.#columnsConfig.sizes = _.merge({
      masschange: {
        width: 1,
        min_width: 1,
        max_width: 1,
      },
      overdue: {
        width: constants.overdue.width,
        min_width: constants.overdue.min_width,
        max_width: constants.overdue.max_width,
      },
      overload: {
        width: constants.overload.width,
        min_width: constants.overload.min_width,
        max_width: constants.overload.max_width,
      },
      comments: {
        width: constants.comments.width,
        min_width: constants.comments.min_width,
        max_width: constants.comments.max_width,
      },
      attachments: {
        width: constants.attachments.width,
        min_width: constants.attachments.min_width,
        max_width: constants.attachments.max_width,
      },
      wbs: {
        width: constants.wbs.width,
        min_width: constants.wbs.min_width,
        max_width: constants.wbs.max_width,
      },
      text: {
        width: 200,
        min_width: 100,
      },
      start_date: {
        width: 130,
        min_width: this.#instance.config.min_grid_column_width,
      },
      end_date: {
        width: 130,
        min_width: this.#instance.config.min_grid_column_width,
      },
      create_date: {
        width: 96,
        min_width: this.#instance.config.min_grid_column_width,
      },
      creator: {
        width: 96,
        min_width: this.#instance.config.min_grid_column_width,
      },
      duration: {
        width: 70,
        min_width: this.#instance.config.min_grid_column_width,
      },
      estimation: {
        width: 70,
        min_width: this.#instance.config.min_grid_column_width,
      },
      time_tracking: {
        width: 140,
        min_width: this.#instance.config.min_grid_column_width,
      },
      progress: {
        width: 70,
        min_width: this.#instance.config.min_grid_column_width,
      },
      resource_id: {
        width: 80,
        min_width: this.#instance.config.min_grid_column_width,
      },
      owner_id: {
        width: 100,
        min_width: this.#instance.config.min_grid_column_width,
      },
      total_price: {
        width: 70,
        min_width: this.#instance.config.min_grid_column_width,
      },
      actual_cost: {
        width: 70,
        min_width: this.#instance.config.min_grid_column_width,
      },
      predecessors: {
        width: 70,
        min_width: this.#instance.config.min_grid_column_width,
      },
      total_float: {
        width: 70,
        min_width: this.#instance.config.min_grid_column_width,
      },
      free_float: {
        width: 70,
        min_width: this.#instance.config.min_grid_column_width,
      },
      buttons: {
        width: constants.buttons.width,
        min_width: constants.buttons.min_width,
        max_width: constants.buttons.max_width,
      },
      default: {
        width: 80,
        min_width: this.#instance.config.min_grid_column_width,
      },
    }, this.#columnsConfig.sizes || {});
  }
}

export default new ColumnsConfig();
